import { useQuery } from "@tanstack/react-query";
import { pathPickMembershipQueryApi } from "@/features/pathpick/features/membership/api/query";
import {
  PathPickMembershipApplication,
  PathPickMembershipApplicationsResponse,
} from "@/features/pathpick/features/membership/types/response";
import { PATHPICK_MEMBERSHIP_APPLICATIONS_QUERY_KEY } from "@/features/pathpick/features/membership/constants";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import { toCamelKeyObject } from "@/utils/case";

// 입금 대기 상태 — 어드민이 입금을 확인하면 다른 상태로 넘어간다.
const PENDING_STATUS = "PENDING";

// 상태 문자열에 enum 스펙이 없어 대소문자를 무시하고 비교한다.
const isPendingStatus = (application: PathPickMembershipApplication) =>
  application.status?.toUpperCase() === PENDING_STATUS;

// 내 멤버십 신청 내역 조회.
// 입금 대기 중인 신청이 있으면 새 플랜 신청을 받지 않아, 화면은 주로 hasPendingApplication만 쓴다.
const useGetPathPickMembershipApplications = () => {
  const { companyId } = useCompanyIdentity();

  const { data, isPending, isFetching } = useQuery({
    queryKey: PATHPICK_MEMBERSHIP_APPLICATIONS_QUERY_KEY(companyId),
    queryFn: async () => {
      const data =
        await pathPickMembershipQueryApi.getPathPickMembershipApplications(
          companyId as number
        );
      return toCamelKeyObject(data) as PathPickMembershipApplicationsResponse;
    },
    enabled: Boolean(companyId),
  });

  const applications = data?.applications;

  // 입금 대기 중인 신청 — 요약 모달이 입금 정보와 접수 내용을 그대로 보여준다.
  // 동시에 두 건이 열릴 일은 없지만, 있다면 가장 최근 신청을 쓴다.
  const pendingApplication = applications
    ?.filter(isPendingStatus)
    .sort((a, b) => (b.appliedAt ?? "").localeCompare(a.appliedAt ?? ""))[0];

  const hasPendingApplication = pendingApplication !== undefined;

  return {
    applications,
    isPending,
    // 신청 직후 재조회 중에는 요약 모달이 빈 표 대신 로딩을 보여준다.
    isFetching,
    pendingApplication,
    hasPendingApplication,
  };
};

export default useGetPathPickMembershipApplications;
